import { useState, useEffect } from 'react'
import z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { FormError } from '../../ui/FormError'
import { salesService } from '../../../services/sales.service'
import { CFDI_CANCEL_MOTIVES, CfdiCancelMotive } from '../../../services/facturama.service'
import { toast } from 'sonner'
import { useQueryClient } from '@tanstack/react-query'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import { AlertTriangle, FileText } from 'lucide-react'

interface CancelSaleModalProps {
  isOpen: boolean
  onClose: () => void
  sale: {
    id: string | number
    paymentMethod: string
    ticketNumber: string
    invoiced?: boolean
    cfdi_id?: string | null
    cfdi_cancelled_at?: string | null
  } | null
}

export function CancelSaleModal({ isOpen, onClose, sale }: CancelSaleModalProps) {
  const queryClient = useQueryClient()

  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Opciones CFDI
  const hasCfdi = !!(sale?.invoiced && sale?.cfdi_id && !sale?.cfdi_cancelled_at)
  const [cancelInvoice, setCancelInvoice] = useState(false)
  const [cfdiMotive, setCfdiMotive] = useState<CfdiCancelMotive>('02')
  const [cfdiUuidReplacement, setCfdiUuidReplacement] = useState('')

  const paymentMethod = sale?.paymentMethod || ''
  const isBankPayment =
    paymentMethod.toLowerCase().includes('tarjeta') ||
    paymentMethod.toLowerCase().includes('transferencia')

  const formSchema = z.object({
    reason: z.string().min(1, 'El motivo de cancelación es obligatorio.'),
    reference: isBankPayment
      ? z.string().min(1, 'El folio o referencia es obligatorio para pagos electrónicos.')
      : z.string().optional(),
  })
  type FormValidate = z.infer<typeof formSchema>

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValidate>({
    resolver: zodResolver(formSchema),
    defaultValues: { reason: '', reference: '' },
    mode: 'onChange',
  })

  // Limpiar al abrir/cerrar
  useEffect(() => {
    if (isOpen) {
      reset({ reason: '', reference: '' })
      setCancelInvoice(false)
      setCfdiMotive('02')
      setCfdiUuidReplacement('')
    }
  }, [isOpen, reset])

  if (!isOpen || !sale) return null

  const onSubmit = async (formData: FormValidate) => {
    // Motivo 01 requiere UUID de sustitución
    if (hasCfdi && cancelInvoice && cfdiMotive === '01' && !cfdiUuidReplacement.trim()) {
      toast.error('El motivo 01 requiere el UUID del CFDI sustituto.')
      return
    }

    setIsSubmitting(true)

    try {
      const generateUUID = () => {
        if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.getRandomValues === 'function') {
          return (1e7.toString() + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c: any) =>
            (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
          )
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8)
          return v.toString(16)
        })
      }

      const idempotencyKey =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : generateUUID()

      const payload: any = {
        idempotency_key: idempotencyKey,
        reason: formData.reason,
        cancel_invoice: hasCfdi ? cancelInvoice : false,
      }

      if (isBankPayment && formData.reference) {
        payload.reference = formData.reference
      }

      if (hasCfdi && cancelInvoice) {
        payload.cfdi_motive = cfdiMotive
        if (cfdiMotive === '01' && cfdiUuidReplacement.trim()) {
          payload.cfdi_uuid_replacement = cfdiUuidReplacement.trim()
        }
      }

      await salesService.cancelSale(sale.id, payload)

      toast.success('La venta fue cancelada correctamente')
      queryClient.invalidateQueries({ queryKey: ['saleDetail', sale.id] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      queryClient.invalidateQueries({ queryKey: ['register-context'] })
      queryClient.invalidateQueries({ queryKey: ['facturama-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['purchase-suggested-orders'] })
      onClose()
    } catch (error: any) {
      console.error(error)
      const msg = error?.response?.data?.message || 'Error al cancelar la venta.'
      toast.error(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cancelar Venta Completa</DialogTitle>
          <DialogDescription>
            Estás a punto de cancelar la venta {sale.ticketNumber}. Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          {/* Motivo de la cancelación */}
          <div className="space-y-2">
            <Label>
              Motivo de la cancelación <span className="text-red-500">*</span>
            </Label>
            <Input
              {...register('reason')}
              placeholder="Ej. Cobro duplicado"
              disabled={isSubmitting}
            />
            {errors.reason && <FormError message={errors.reason.message} />}
            <div className="flex flex-wrap gap-2 mt-2">
              {['Cobro duplicado', 'Error de captura', 'Cliente se arrepintió'].map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setValue('reason', tag, { shouldValidate: true })}
                  className="text-[10px] px-2 py-1 rounded-full border transition-colors bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Referencia bancaria */}
          {isBankPayment && (
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <Label>
                Folio de reversa / referencia ({sale.paymentMethod}){' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                {...register('reference')}
                placeholder={
                  paymentMethod.toLowerCase().includes('tarjeta')
                    ? 'Ej. REVERSA-BBVA-458796'
                    : 'Ej. SPEI-REVERSA-2026'
                }
                disabled={isSubmitting}
              />
              {errors.reference && <FormError message={errors.reference.message} />}
              <p className="text-[10px] text-slate-500">
                Al ser un pago electrónico, debes procesar la devolución en tu terminal o banco y anotar aquí el folio.
              </p>
            </div>
          )}

          {/* Sección CFDI — solo si la venta tiene factura vigente */}
          {hasCfdi && (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs font-medium text-amber-800">
                  Esta venta tiene una factura CFDI emitida. ¿Deseas cancelarla también ante el SAT?
                </p>
              </div>

              {/* Checkbox cancelar CFDI */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={cancelInvoice}
                  onChange={(e) => setCancelInvoice(e.target.checked)}
                  disabled={isSubmitting}
                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-slate-700">
                  Cancelar también la factura (CFDI) ante el SAT
                </span>
              </label>

              {cancelInvoice && (
                <div className="space-y-3 pl-6">
                  {/* Motivo SAT */}
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Motivo de cancelación SAT <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={cfdiMotive}
                      onValueChange={(v) => {
                        setCfdiMotive(v as CfdiCancelMotive)
                        if (v !== '01') setCfdiUuidReplacement('')
                      }}
                    >
                      <SelectTrigger className="bg-white text-xs h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CFDI_CANCEL_MOTIVES.map((m) => (
                          <SelectItem key={m.value} value={m.value} className="text-xs whitespace-normal max-w-full pr-2">
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* UUID sustituto — solo motivo 01 */}
                  {cfdiMotive === '01' && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">
                        UUID del CFDI sustituto <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        value={cfdiUuidReplacement}
                        onChange={(e) => setCfdiUuidReplacement(e.target.value)}
                        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        disabled={isSubmitting}
                        className="h-9 font-mono text-xs bg-white"
                      />
                    </div>
                  )}

                  <div className="flex items-start gap-1.5 text-[10px] text-amber-700">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>
                      Esta acción cancela la factura ante el SAT. No puede deshacerse.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="flex gap-3 pt-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              variant="destructive"
              className="flex-1"
            >
              {isSubmitting ? 'Procesando...' : 'Confirmar Cancelación'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={isSubmitting}
            >
              Cerrar
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
